import { ref } from "vue";
import type {
  InventoryEntry,
  LoadoutPreset,
  LoadoutPresetApplyResponse,
  LoadoutPresetClearResponse,
  LoadoutPresetRenameResponse,
  LoadoutPresetSaveResponse,
  LoadoutPresetsResponse
} from "@idle-rpg/shared";
import {
  apiApplyLoadoutPreset,
  apiClearLoadoutPreset,
  apiLoadoutPresets,
  apiRenameLoadoutPreset,
  apiSaveLoadoutPreset
} from "../../../api/client";
import { useSessionStore } from "../../../store/session";

const state = ref<LoadoutPresetsResponse | null>(null);
const loading = ref(false);
const error = ref<string | null>(null);
const busyPresetId = ref<string | null>(null);

function applyCharacterSnapshot(
  session: ReturnType<typeof useSessionStore>,
  inventory: InventoryEntry[],
  equipment: Record<string, InventoryEntry | null>
) {
  if (!session.character) return;
  session.character.inventory = inventory as InventoryEntry[];
  session.character.equipment = equipment as any;
  const idx = session.characters.findIndex((c) => c.id === session.character!.id);
  if (idx !== -1) {
    session.characters[idx].inventory = inventory as InventoryEntry[];
    session.characters[idx].equipment = equipment as any;
  }
}

function mergePreset(
  nextPreset: LoadoutPreset,
  activePresetId: string | null
) {
  if (!state.value) return;
  const list = [...state.value.presets];
  const idx = list.findIndex((preset) => preset.id === nextPreset.id);
  if (idx !== -1) {
    list[idx] = nextPreset;
  } else {
    list.push(nextPreset);
  }
  const normalized = list.map((preset) => ({
    ...preset,
    isActive: activePresetId ? preset.id === activePresetId : preset.isActive
  }));
  state.value = {
    presets: normalized.sort((a, b) => a.slotIndex - b.slotIndex),
    activePresetId
  };
}

export function useLoadoutPresetsState() {
  const session = useSessionStore();

  async function refresh(options?: { force?: boolean }) {
    if (!session.token || !session.character) return;
    if (state.value && !options?.force) return;
    loading.value = true;
    error.value = null;
    const res = await apiLoadoutPresets(session.token, session.character.id);
    loading.value = false;
    if (!res.ok || !res.data) {
      error.value = "error" in res ? res.error?.message || "Failed to load presets" : "Failed to load presets";
      return;
    }
    state.value = res.data as LoadoutPresetsResponse;
  }

  async function save(presetId: string): Promise<LoadoutPresetSaveResponse | null> {
    if (!session.token || !session.character) return null;
    busyPresetId.value = presetId;
    error.value = null;
    const res = await apiSaveLoadoutPreset(session.token, session.character.id, presetId, {
      clientRequestId: crypto.randomUUID()
    });
    busyPresetId.value = null;
    if (!res.ok || !res.data) {
      error.value = "error" in res ? res.error?.message || "Failed to save preset" : "Failed to save preset";
      return null;
    }
    const data = res.data as LoadoutPresetSaveResponse;
    applyCharacterSnapshot(session, data.inventory, data.equipment);
    if (state.value) {
      mergePreset(data.preset, data.activePresetId);
    } else {
      await refresh({ force: true });
    }
    return data;
  }

  async function apply(presetId: string): Promise<LoadoutPresetApplyResponse | null> {
    if (!session.token || !session.character) return null;
    busyPresetId.value = presetId;
    error.value = null;
    const res = await apiApplyLoadoutPreset(session.token, session.character.id, presetId, {
      clientRequestId: crypto.randomUUID()
    });
    busyPresetId.value = null;
    if (!res.ok || !res.data) {
      error.value = "error" in res ? res.error?.message || "Failed to apply preset" : "Failed to apply preset";
      return null;
    }
    const data = res.data as LoadoutPresetApplyResponse;
    applyCharacterSnapshot(session, data.inventory, data.equipment);
    if (state.value) {
      mergePreset(data.preset, data.activePresetId);
    } else {
      await refresh({ force: true });
    }
    return data;
  }

  async function rename(presetId: string, name: string): Promise<LoadoutPresetRenameResponse | null> {
    if (!session.token || !session.character) return null;
    busyPresetId.value = presetId;
    error.value = null;
    const res = await apiRenameLoadoutPreset(session.token, session.character.id, presetId, {
      name,
      clientRequestId: crypto.randomUUID()
    });
    busyPresetId.value = null;
    if (!res.ok || !res.data) {
      error.value = "error" in res ? res.error?.message || "Failed to rename preset" : "Failed to rename preset";
      return null;
    }
    const data = res.data as LoadoutPresetRenameResponse;
    if (state.value) {
      mergePreset(data.preset, data.activePresetId);
    } else {
      await refresh({ force: true });
    }
    return data;
  }

  async function clear(presetId: string): Promise<LoadoutPresetClearResponse | null> {
    if (!session.token || !session.character) return null;
    busyPresetId.value = presetId;
    error.value = null;
    const res = await apiClearLoadoutPreset(session.token, session.character.id, presetId, {
      clientRequestId: crypto.randomUUID()
    });
    busyPresetId.value = null;
    if (!res.ok || !res.data) {
      error.value = "error" in res ? res.error?.message || "Failed to clear preset" : "Failed to clear preset";
      return null;
    }
    const data = res.data as LoadoutPresetClearResponse;
    if (state.value) {
      mergePreset(data.preset, data.activePresetId);
    } else {
      await refresh({ force: true });
    }
    return data;
  }

  function clearState() {
    state.value = null;
    loading.value = false;
    error.value = null;
    busyPresetId.value = null;
  }

  return {
    state,
    loading,
    error,
    busyPresetId,
    refresh,
    save,
    apply,
    rename,
    clear,
    clearState
  };
}
